import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, Copy, ArrowRight, ShieldCheck, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { motion } from 'framer-motion';

export const SuccessFiling = () => {
  const [trackingId, setTrackingId] = useState('');
  const { toast } = useToast();

  useEffect(() => {
    window.scrollTo(0, 0);
    const stored = localStorage.getItem('filing_tracking_id');
    if (stored) {
      setTrackingId(stored);
    } else {
      // Fallback
      const date = new Date();
      const dateStr = `${date.getFullYear()}${(date.getMonth() + 1).toString().padStart(2, '0')}${date.getDate().toString().padStart(2, '0')}`;
      const randomBits = Math.random().toString(36).substring(2, 6).toUpperCase();
      setTrackingId(`TRK-FBR-${dateStr}-${randomBits}`);
    }
  }, []);

  const copyToClipboard = () => {
    navigator.clipboard.writeText(trackingId);
    toast({
      title: "Tracking ID Copied",
      description: "You can use this ID for reference in support tickets.",
    });
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="w-full max-w-2xl pt-20 pb-20">
        
        <motion.div 
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="bg-white rounded-[2.5rem] shadow-xl shadow-slate-200/50 border border-slate-100 p-8 md:p-12 text-center"
        >
          {/* Animated Success Icon */}
          <motion.div 
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring", stiffness: 200, damping: 15, delay: 0.2 }}
            className="w-24 h-24 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-8 shadow-inner shadow-emerald-200"
          >
            <CheckCircle2 className="w-12 h-12 text-emerald-600" />
          </motion.div>

          <h1 className="text-3xl md:text-5xl font-black text-slate-800 tracking-tight mb-4">
            Declaration Submitted!
          </h1>
          <p className="text-lg text-slate-500 font-medium mb-10 max-w-lg mx-auto leading-relaxed">
            Your formal FBR Tax Declaration and Wealth Statement have been securely vaulted and forwarded for official filing.
          </p>

          {/* Tracking Ticket */}
          <div className="bg-slate-50 border-2 border-slate-100 rounded-[1.5rem] p-6 mb-10 relative overflow-hidden">
             <div className="absolute top-0 right-0 bg-blue-100 px-4 py-1 rounded-bl-xl text-blue-700 font-bold text-xs uppercase tracking-widest">
                Official Receipt
             </div>
             
             <p className="text-slate-500 font-bold tracking-widest uppercase text-xs mb-2">Primary Tracking ID</p>
             <div className="flex flex-col md:flex-row items-center justify-center gap-4">
               <span className="text-3xl font-black text-indigo-900 tracking-wider font-mono">
                 {trackingId}
               </span>
               <Button onClick={copyToClipboard} variant="outline" size="sm" className="rounded-full bg-white h-10 px-4">
                 <Copy className="w-4 h-4 mr-2 text-slate-500" /> Copy ID
               </Button>
             </div>
             
             <div className="mt-8 pt-6 border-t border-slate-200 grid grid-cols-2 gap-4 text-left">
                <div className="flex items-center gap-3">
                   <ShieldCheck className="w-8 h-8 text-emerald-500 shrink-0" />
                   <div>
                      <p className="text-[10px] font-bold text-slate-400 uppercase">Data Status</p>
                      <p className="font-semibold text-slate-700 text-sm">256-Bit Secured</p>
                   </div>
                </div>
                <div className="flex items-center gap-3">
                   <CheckCircle2 className="w-8 h-8 text-emerald-500 shrink-0" />
                   <div>
                      <p className="text-[10px] font-bold text-slate-400 uppercase">Queue Position</p>
                      <p className="font-semibold text-slate-700 text-sm">Priority Filing</p>
                   </div>
                </div>
             </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild variant="outline" className="h-14 rounded-2xl font-bold px-8 border-2 border-slate-200 text-slate-700">
              <Link to="/dashboard?tab=vault">
                 <Download className="w-5 h-5 mr-2" /> View Vault
              </Link>
            </Button>
            <Button asChild className="h-14 bg-indigo-600 hover:bg-indigo-700 rounded-2xl font-black text-lg px-10 shadow-lg shadow-indigo-200">
              <Link to="/dashboard">
                 Return to Dashboard <ArrowRight className="w-5 h-5 ml-2" />
              </Link>
            </Button>
          </div>

        </motion.div>

        <p className="text-center text-slate-400 text-sm font-medium mt-8">
          Need help? Contact our support team with your Tracking ID.
        </p>
      </div>
    </div>
  );
};
